import { Combobox, Paper, useCombobox } from "@mantine/core";
import { useEffect, useImperativeHandle } from "react";

interface SuggestionPopupProps {
  items: string[];
  command: (command: { id: string }) => void;
  posX: number;
  posY: number;
  ref?: React.Ref<SuggestionPopupHandle>;
}

interface SuggestionPopupHandle {
  onKeyDown: (event: KeyboardEvent) => boolean;
}

function SuggestionPopup({
  items,
  command,
  posX,
  posY,
  ref
}: SuggestionPopupProps) {
  const combobox = useCombobox();

  useEffect(() => {
    combobox.updateSelectedOptionIndex();
  }, [combobox, items]);

  useImperativeHandle(ref, () => ({
    onKeyDown: (event: KeyboardEvent) => {
      if (event.key === "ArrowUp") {
        combobox.selectPreviousOption();
        return true;
      }

      if (event.key === "ArrowDown") {
        combobox.selectNextOption();
        return true;
      }

      if (event.key === "Enter") {
        combobox.clickSelectedOption();
        return true;
      }

      return false;
    }
  }));

  const handleSelection = (value: string) => {
    const index = parseInt(value);
    const item = items[index];

    if (item) {
      command({ id: item });
    }
  };

  return (
    <Combobox size="sm" store={combobox} onOptionSubmit={handleSelection}>
      <Combobox.EventsTarget>
        {/* Mantine requires a child node here, but we are controlling the combobox from outside via onKeyDown */}
        <div className="empty-target" />
      </Combobox.EventsTarget>

      <Paper
        p="sm"
        pos="absolute"
        radius="sm"
        shadow="sm"
        // if we'd use `left` directly, that would translate to Mantine spacing scale & mess up the positioning
        // interestingly, `top` does not have this issue
        style={{ left: posX, zIndex: 10000 }}
        top={posY}
        withBorder
      >
        <Combobox.Options>
          {items.length ? (
            items.map((item, index) => (
              <Combobox.Option key={index} value={index.toString()}>
                {item}
              </Combobox.Option>
            ))
          ) : (
            <Combobox.Empty>Keine Ergebnisse ...</Combobox.Empty>
          )}
        </Combobox.Options>
      </Paper>
    </Combobox>
  );
}

export { SuggestionPopup, SuggestionPopupHandle, SuggestionPopupProps };
